import { SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

/**
 * Interface for validation result
 */
export interface ValidationResult {
  isValid: boolean;
  sanitized: string;
}

/**
 * Validates user input to detect and block XSS attempts
 * @param input - The user input to validate
 * @returns Object with isValid flag and sanitized text if valid
 */
export function validateUserInput(input: string): ValidationResult {
  if (!input) {
    return { isValid: false, sanitized: '' };
  }

  const trimmedInput = input.trim();

  // Check for script tags (case-insensitive) - most direct check
  // This will catch <script>, <SCRIPT>, <script>, </script>, <script (without closing >), etc.
  const hasScriptTag = /<[\s]*\/?[\s]*script/gi.test(trimmedInput);

  // Check for any HTML tags that could contain scripts
  const htmlTagPattern = /<[\s]*\/?[\s]*(script|iframe|object|embed|form|input|button|link|meta|style|svg|img|body|html)[\s\S]*?>/gi;

  // Check for event handlers (onerror, onload, onclick, etc.)
  const eventHandlerPattern = /on\w+\s*=/gi;

  // Check for javascript: protocol
  const javascriptProtocolPattern = /javascript\s*:/gi;

  // Check for dangerous functions
  const dangerousFunctionPattern = /(eval|expression|setTimeout|setInterval|Function|constructor)\s*\(/gi;

  // Check for encoded script tags (common XSS evasion techniques)
  const encodedScriptPattern = /(&lt;|%3C|&#60;)[\s]*script/gi;

  // Combined check - if any pattern matches, block the input
  if (
    hasScriptTag ||
    htmlTagPattern.test(trimmedInput) ||
    eventHandlerPattern.test(trimmedInput) ||
    javascriptProtocolPattern.test(trimmedInput) ||
    dangerousFunctionPattern.test(trimmedInput) ||
    encodedScriptPattern.test(trimmedInput)
  ) {
    console.warn('Blocked potentially malicious input:', trimmedInput.substring(0, 100));
    return { isValid: false, sanitized: '' };
  }

  // If no dangerous patterns found, strip any remaining HTML tags and return plain text
  const tempDiv = document.createElement('div');
  tempDiv.textContent = trimmedInput;
  const sanitized = (tempDiv.textContent || tempDiv.innerText || '').trim();

  return { isValid: true, sanitized };
}

/**
 * Sanitizes HTML content to prevent XSS attacks when rendering
 * @param content - The HTML content to sanitize
 * @param sanitizer - Angular's DomSanitizer instance
 * @returns Sanitized HTML string safe for rendering
 */
export function sanitizeMessageContent(content: string, sanitizer: DomSanitizer): string {
  if (!content) {
    return '';
  }
  // Sanitize the content to remove dangerous HTML/JS
  const sanitized = sanitizer.sanitize(SecurityContext.HTML, content);
  return sanitized || '';
}

/**
 * Strict plain-text sanitizer: rejects any HTML-like or encoded tag patterns.
 * Use for input fields like vault names where HTML should never be allowed.
 */
export function sanitizePlainTextStrict(input: string): ValidationResult {
  if (!input) {
    return { isValid: false, sanitized: '' };
  }
  const trimmed = input.trim();

  // Reject any angle-bracketed content or encoded variants
  const htmlLikePattern = /<[^>]+>/gi;
  const encodedTagPattern = /(&lt;|&gt;|%3C|%3E|&#60;|&#62;)/gi;

  if (htmlLikePattern.test(trimmed) || encodedTagPattern.test(trimmed)) {
    return { isValid: false, sanitized: '' };
  }

  const sanitized = stripHtmlTags(trimmed);
  if (!sanitized) {
    return { isValid: false, sanitized: '' };
  }

  return { isValid: true, sanitized };
}

/**
 * Strips HTML tags from user input and returns plain text
 * Use this for input fields like entity names, vault names, etc.
 * @param input - The user input that may contain HTML
 * @returns Plain text with all HTML tags removed
 */
export function stripHtmlTags(input: string): string {
  if (!input) {
    return '';
  }
  // Quick block for obvious script tags before parsing
  const scriptPattern = /<\s*\/?\s*script[\s\S]*?>/gi;
  if (scriptPattern.test(input)) {
    return '';
  }

  // Use a temporary div: put input into innerHTML, then read textContent to strip tags.
  // This avoids returning raw markup like "<h1>text</h1>".
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = input;
  return (tempDiv.textContent || tempDiv.innerText || '').trim();
}

